import { useDispatch } from 'react-redux';

// material-ui
import { Button, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputLabel, Stack, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider, FormikValues } from 'formik';

// project imports
import IconButton from 'components/@extended/IconButton';
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import { CloseCircleOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Company,
  MerchantBankAccount,
  UserBankAccount,
  useBankByRoutingNumberLazyQuery,
  useDeleteMerchantBankAccountMutation,
  useDeleteUserBankAccountMutation,
  useSaveMerchantBankAccountMutation,
  useSaveUserBankAccountMutation
} from '../../graphql/types';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { isValidRoutingNumber } from '../../utils/bankAccounts';
import BankInfoCell, {BankInfoCellData} from 'components/ag-grid/BankInfoCell';
import ActionConfirmation from 'components/mca/ActionConfirmation';
import CustomTooltip from '../../components/CustomTooltip';

type BankAccountEntity = Pick<MerchantBankAccount | UserBankAccount, 'id' | 'accountNumber' | 'routingNumber' | 'name'>;

const getInitialValues = (entity: FormikValues | null) => {
  const newEntity = {
    accountNumber: '',
    routingNumber: '',
    accountNumberConfirm: ''
  };

  if (entity) {
    newEntity.accountNumber = entity.accountNumber || '';
    newEntity.routingNumber = entity.routingNumber || '';
    newEntity.accountNumberConfirm = '';
    return _.merge({}, newEntity, entity);
  }

  return newEntity;
};

export interface Props {
  merchant?: Company;
  userId?: string;
  entity: BankAccountEntity | null;
  onCancel: () => void;
  onSave: (bankAccount: Partial<BankAccountEntity> | null) => void;
  deferPersistence?: boolean;
}

const AddMerchantBankAccount = ({ merchant, userId, entity, onCancel, onSave, deferPersistence }: Props) => {
  const dispatch = useDispatch();
  const [saveMerchantBankAccount] = useSaveMerchantBankAccountMutation();
  const [saveUserBankAccount] = useSaveUserBankAccountMutation();
  const [deleteMerchantBankAccount] = useDeleteMerchantBankAccountMutation();
  const [deleteUserBankAccount] = useDeleteUserBankAccountMutation();
  const [fetchBankByRoutingNumber] = useBankByRoutingNumberLazyQuery({ fetchPolicy: 'network-only' });

  const [bankInfoData, setBankInfoData] = useState<BankInfoCellData | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const CustomerSchema = Yup.object().shape({
    accountNumber: Yup.string()
      .matches(/^[0-9]+$/, 'Must be at least 4 digits')
      .min(4, 'Must be at least 4 digits')
      .max(17, 'Must be at most 17 digits')
      .required('Account # is required'),
    accountNumberConfirm: Yup.string()
      .oneOf([Yup.ref('accountNumber'), ''], 'Account numbers must match')
      .required('Account # confirmation is required'),
    routingNumber: Yup.string()
      .matches(/[0-9]+/gi, 'Must be 9 digits, valid ABA routing number')
      .test('routing-number-validation', 'Must be 9 digits, valid ABA routing number', isValidRoutingNumber)
      .required('Routing # is required')
  });

  const deleteHandler = async () => {
    try {
      const id = entity!.id as string;
      if (userId) {
        await deleteUserBankAccount({ variables: { id } });
      } else {
        await deleteMerchantBankAccount({ variables: { id } });
      }
      setConfirmDelete(false);
      onSave(entity);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Bank Account deleted successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  const prevent = (e: SyntheticEvent) => {
    e.preventDefault();
    return false;
  };

  const formik = useFormik({
    initialValues: getInitialValues(entity!),
    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const dto = {
          id: entity?.id || null,
          routingNumber: values.routingNumber,
          accountNumber: values.accountNumber
        };
        if (deferPersistence) {
          onSave({
            id: entity?.id || null,
            routingNumber: values.routingNumber,
            accountNumber: values.accountNumber,
            name: entity?.name
          });
          if (entity) {
            dispatch(
              openSnackbar({
                open: true,
                message: 'Bank account updated.',
                variant: 'alert',
                alert: { color: 'success' },
                close: true
              })
            );
          }
          setSubmitting(false);
          return;
        }
        if (userId) {
          await saveUserBankAccount({ variables: { userId, dto } });
        } else if (merchant?.id) {
          await saveMerchantBankAccount({ variables: { merchantId: merchant.id, dto } });
        } else {
          setSubmitting(false);
          return;
        }
        onSave(values);
        dispatch(
          openSnackbar({
            open: true,
            message: entity ? 'Bank Account updated successfully.' : 'Bank Account added successfully.',
            variant: 'alert',
            alert: { color: 'success' },
            close: true
          })
        );
        setSubmitting(false);
      } catch (error) {
        console.error(error);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, values } = formik;

  useEffect(() => {
    const rn = values.routingNumber;
    if (!rn || rn.length !== 9) {
      setBankInfoData(null);
      return;
    }
    fetchBankByRoutingNumber({ variables: { routingNumber: rn } }).then((res) => {
      const bank = res.data?.bankByRoutingNumber;
      setBankInfoData(bank ? { bankName: bank.bankName, bankLogo: bank.logo, locationName: '' } : null);
    });
  }, [values.routingNumber]);

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>{entity ? 'Edit Bank Account' : 'New Bank Account'}</DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Stack height="100%" justifyContent="center" pl={0.5}>
                    <BankInfoCell data={bankInfoData} showUnknown={!bankInfoData && values.routingNumber.length === 9} />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.5}>
                    <InputLabel required htmlFor="mba-entity-routingNumber">
                      Routing #
                    </InputLabel>
                    <TextField
                      size="small"
                      fullWidth
                      id="mba-entity-routingNumber"
                      placeholder="Enter Routing #"
                      {...getFieldProps('routingNumber')}
                      error={Boolean(touched.routingNumber && errors.routingNumber)}
                      helperText={touched.routingNumber && errors.routingNumber}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.5}>
                    <InputLabel required htmlFor="mba-entity-accountNumber">
                      Account #
                    </InputLabel>
                    <TextField
                      size="small"
                      fullWidth
                      id="mba-entity-accountNumber"
                      placeholder="Enter Account #"
                      {...getFieldProps('accountNumber')}
                      error={Boolean(touched.accountNumber && errors.accountNumber)}
                      helperText={touched.accountNumber && errors.accountNumber}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.5}>
                    <InputLabel required htmlFor="mba-entity-accountNumberConfirm">
                      Account # Confirmation
                    </InputLabel>
                    <TextField
                      size="small"
                      fullWidth
                      id="mba-entity-accountNumberConfirm"
                      placeholder="Enter Account # Confirmation"
                      {...getFieldProps('accountNumberConfirm')}
                      error={Boolean(touched.accountNumberConfirm && errors.accountNumberConfirm)}
                      helperText={touched.accountNumberConfirm && errors.accountNumberConfirm}
                      onPaste={prevent}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  {entity?.id && !deferPersistence && (
                    <CustomTooltip title="Delete Bank Account" placement="top">
                      <IconButton onClick={() => setConfirmDelete(true)} size="large" color="error">
                        <DeleteOutlined />
                      </IconButton>
                    </CustomTooltip>
                  )}
                </Grid>
                <Grid item>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button fullWidth color="error" startIcon={<CloseCircleOutlined />} onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button fullWidth type="submit" variant="contained"  disabled={isSubmitting} startIcon={entity ? <SaveOutlined /> : <PlusOutlined />}>
                      {entity ? 'Save' : 'Add'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>

      <ActionConfirmation
        title={`Deleted bank account details cannot be restored. Would you like to proceed?`}
        type="error"
        useYesNoButtonLabels={true}
        open={confirmDelete}
        handleClose={(confirmed) => {
          if (confirmed) deleteHandler();
          else setConfirmDelete(false);
        }}
      />
    </>
  );
};

export default AddMerchantBankAccount;
